// PoolPill — the shared GTM header "pool" pill: a single number + unit with a
// tooltip carrying the detail (ceiling, reset, etc.). Reused by the Apollo credit
// pill and (later) the Instantly quota pill, so the number/unit/tone/live-dot and
// the click affordance live here once.
//
// Props:
//   label     small muted prefix (e.g. 'Apollo')
//   value     the single number to show (already formatted, e.g. '198')
//   unit      trailing muted unit (e.g. 'credits')
//   tone      'neutral' | 'amber' | 'red'  (colors the number + border)
//   tooltip   MUI tooltip content (ceiling / reset / internal-only note)
//   live      show the pulsing live dot
//   clickable pointer cursor + hover lift (admins only, for the editor)
//   onClick   click handler (opens the editor popover)
//   dim       fade the figures on a stale/reconnecting stream
//   trailing  optional node rendered at the end (e.g. an edit pencil)

import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { THEME } from './genesisData';

const pulseSx = {
  '@keyframes poolPillPulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.4 },
    '100%': { opacity: 1 },
  },
};

const TONES = {
  red: { color: THEME.pink, border: 'rgba(198,61,87,0.5)', bg: 'rgba(198,61,87,0.12)' },
  amber: { color: THEME.amber, border: 'rgba(251,191,36,0.4)', bg: THEME.amberDim },
  neutral: { color: THEME.textSecondary, border: THEME.borderStrong, bg: 'rgba(255,255,255,0.04)' },
};

const PoolPill = ({ label, value, unit, tone = 'neutral', tooltip, live, clickable, onClick, dim, trailing }) => {
  const t = TONES[tone] || TONES.neutral;
  return (
    <Tooltip title={tooltip || ''} arrow placement="bottom">
      <Box
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          border: `1px solid ${t.border}`,
          background: t.bg,
          borderRadius: '6px',
          px: '10px',
          py: '5px',
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          cursor: clickable ? 'pointer' : 'default',
          transition: 'border-color 120ms, filter 120ms',
          opacity: dim ? 0.55 : 1,
          '&:hover': clickable ? { borderColor: t.color, filter: 'brightness(1.2)' } : undefined,
          '&:hover .pool-pill-trailing': clickable ? { color: t.color } : undefined,
          ...pulseSx,
        }}
      >
        {live && (
          <Box
            component="span"
            sx={{ width: 6, height: 6, borderRadius: '50%', background: THEME.mint, flexShrink: 0, animation: 'poolPillPulse 2s ease-in-out infinite' }}
          />
        )}
        {label && (
          <Box component="span" sx={{ color: THEME.textMuted, fontWeight: 700, letterSpacing: '0.03em' }}>
            {label}
          </Box>
        )}
        <Box component="span" sx={{ color: t.color, fontWeight: 700 }}>
          {value}
        </Box>
        {unit && (
          <Box component="span" sx={{ color: THEME.textMuted, fontWeight: 500 }}>
            {unit}
          </Box>
        )}
        {trailing}
      </Box>
    </Tooltip>
  );
};

export default PoolPill;
